<template>
    <div>
        <div class="d-flex align-items-center justify-content-between mb-3" id="container-options">
            <div>
                <input type="checkbox" style="margin: 0 22px;" id="groups-all"
                    :checked="allGroupsSelected"
                    @click="handleAllGroups"
                    v-if="groups.data.length"
                />
                <div class="btn-group">
                    <button class="btn btn-primary"
                        :disabled="!enableSendInvite"
                        @click="openBulkInvitesModal"
                        style="border-top-right-radius: 4px; border-bottom-right-radius: 4px; margin-right: 5px;">
                        Send Invite
                    </button>

                    <button class="btn btn-danger"
                        v-if="!isLocationUser"
                        :disabled="!enableDeleteButton"
                        @click="deleteSelectedGroups"
                        style="border-radius: 4px; border-bottom-left-radius: 4px; margin-right: 5px;">
                        Delete
                    </button>

                    <button class="btn btn-info"
                        v-if="!isLocationUser"
                        :disabled="!enableBlockButton"
                        @click="blockSelectedGroups"
                        style="border-top-left-radius: 4px; border-bottom-left-radius: 4px;">
                        Block
                    </button>
                </div>
                <label class="ml-2" v-if="selectedGroups.length">
                    {{ selectedGroups.length }} selected
                </label>
            </div>
            <div class="d-flex justify-content-between">
                <button class="btn btn-primary pull-right" @click="addGroup" style="margin-left: 10px;">
                    <i class="fa fa-plus"></i> New
                </button>
            </div>
        </div>

        <card-spinner style="height: 240px;" v-if="loadingGroups"></card-spinner>

        <div v-if="!loadingGroups">
            <contact-groups-list v-for="(group, index) of groups.data" :key="groups.id" :group="group"
                               :selectedGroups="selectedGroups" :locationUser="isLocationUser"
                               :showBlock="isTeamOwner || isCompanyAdmin" @selected="selectGroup"
                               @updated="updateGroup" @deleted="deleteGroup(group, index)"
                               @openModal="openEditModal"></contact-groups-list>
        </div>
        <pagination class="mt-3" :meta="groups" :links="groups" @page="gotoPage"></pagination>
        <group-edit ref="edit" :group="group" @saved="fetchGroups"></group-edit>

        <send-group-invites-modal ref="openGroupInvitesModal" :locations="locations" :groups="selectedGroups"
                                 @unselectAll="unselectAll"></send-group-invites-modal>
    </div>
</template>


<script>
    import _ from 'lodash';
    import {mapState, mapMutations} from 'vuex';
    import mutations from '../../store/mutation-types';

    export default {
        data() {
            return {
                groups        : {
                    data : [],
                    meta : null,
                    links: null,
                },
                loading       : true,
                loadingGroups : true,
                group         : null,
                sortBy        : 'name|asc',
                selectedGroups: [],
                locations     : []
            }
        },

        created() {
            this.fetchLocations();
            this.fetchGroups().then(() => this.loadingGroups = false);
            Bus.$on('LoadGroupList', () => this.fetchGroups());
        },

        mounted() {
            this.update(this.nameQueryString);
        },

        computed: {
            ...mapState({
                filter: state => state.filter,
            }),

            nameQueryString() {
                const params = (new URL(window.document.location)).searchParams;
                return {search: params.get('name')}
            },

            allGroupsSelected() {
                return _.difference(this.groups.data.map(c => c.id), this.selectedGroups).length === 0;
            },

            isLocationUser() {
                return this.spark.state.user.is_location_user;
            },

            enableSendInvite() {
                return this.selectedGroups.length;
            },

            enableDeleteButton() {
                return this.selectedGroups.length && !this.isLocationUser;
            },

            enableBlockButton() {
                return this.selectedGroups.length && !this.isLocationUser;
            },

            isTeamOwner() {
                return this.spark.state.user.is_team_owner;
            },

            isCompanyAdmin() {
                return this.spark.state.user.is_company_admin;
            },
        },

        watch: {
            filter() {
                this.fetchGroups();
            }
        },

        methods: {
            ...mapMutations({
                update: mutations.UPDATE_FILTER,
            }),

            fetchLocations() {
                return this.$http.get(this.$laroute.route('api.locations.index'))
                    .then(({data}) => {
                        this.locations = data.data;
                    });
            },

            fetchGroups(page = 1) {
                this.loading = true;
                this.loadingGroups = true;
                let params   = {
                    filter: {...this.filter},
                    page  : page,
                    sortBy: this.sortBy
                }
                return this.$http.get('/groups/',{params : params})
                    .then(({data}) => {
                        this.loading  = false;
                        this.loadingGroups = false;
                        this.groups = data;
                        this.groups.prev = this.groups.prev_page_url;
                        this.groups.next = this.groups.next_page_url;
                    });
            },

            updateGroup(group) {
                this.$http.put('/groups', {group: group})
                    .catch(({response}) => this.$alert({
                        message: response.data.message || 'Something went wrong...',
                        type   : 'error',
                        title  : 'Oops!'
                    }));
            },

            deleteNotGroupAllContacts() {
                this.$alert({
                    message: 'Deleting the All Contacts group is not allowed.',
                    type: 'warning'
                });
            },

            deleteGroup(group) {
                this.$http.delete('/groups/' + group);
            },

            async deleteSelectedGroups(checkAllGroupAllContacts = true) {
                if (checkAllGroupAllContacts) {
                    let uri = this.$laroute.route(
                        'groups.has_all_groups',
                        {groups: this.selectedGroups}
                    );
                    let {data} = await axios.get(uri);
                    if (!data.has_group_all_contacts) {
                        this.deleteNotGroupAllContacts();
                        return;
                    }
                }

                this.$confirm().then(() => {
                    this.selectedGroups.forEach(group => {
                        this.deleteGroup(group);
                    });
                    this.selectedGroups = [];
                    const message = 'Group(s) deleted.';
                    this.$alert({message, type: 'success', title: 'Success!'});
                    Bus.$emit('LoadGroupList');
                }).catch(err => console.log('err from delete', err));
            },

            blockGroup(group) {
                this.$http.put('/groups/' + group + '/block');
            },

            async blockSelectedGroups() {
              let options = {
                text             : 'Blocking these Groups will prevent their contacts from receiving future invites.',
                confirmButtonText: 'Yes, Block Contacts',
              };
              this.$confirm(options).then(() => {
                this.selectedGroups.forEach(group => {
                    this.blockGroup(group);
                });
                this.selectedGroups = [];
                this.$alert({message:'Groups have been blocked', type: 'success', title: 'Success!'});
                Bus.$emit('LoadGroupList');
              }).catch(err => console.log('err from block', err));
            },

            gotoPage(page) {
                this.loadingGroups = true;
                this.fetchGroups(page).then(() => this.loadingGroups = false);
            },

            openEditModal(group) {
                this.$refs.edit.open()
                this.group = group
            },

            handleAllGroups() {
                if (this.allGroupsSelected) {
                    this.selectedGroups = this.selectedGroups.filter(sc => !this.groups.data.map(c => c.id).includes(sc));
                    return;
                }

                this.groups.data.map(group => {
                    if (!this.selectedGroups.includes(group.id)) {
                        this.selectedGroups.push(group.id);
                    }
                });
            },

            openBulkInvitesModal() {
                this.$refs.openGroupInvitesModal.open();
            },

            unselectAll() {
                this.selectedGroups = [];
            },

            selectGroup(id) {
                return this.selectedGroups.includes(id)
                    ? this.selectedGroups.splice(this.selectedGroups.indexOf(id), 1)
                    : this.selectedGroups.push(id)
            },

            addGroup() {
                Bus.$emit('openAddNewGroupModal', false);
            }
        }
    }
</script>

<style lang="scss" scoped>
    @media screen and (max-width: 991px) {
        #container-options {
            flex-direction: column-reverse;
        }
        #options {
            flex-direction: column !important;
        }
        .select-filter {
            margin-bottom: 1em;
        }
    }
</style>
